import React from 'react';
import { useAppSelector } from 'store/hooks';
import { Box, Grid } from '@mui/material';
import { Permission, Resource } from '@pepperhq/auth-client';

import {
    LocationAddressCard,
    LocationGeneralCard,
    LocationInAppLinksCard,
    LocationServicesCard,
    LocationTablesAndZonesCard
} from 'components/location/cards';
import { getLocationConfigFromSettings, Location } from 'components/location/models/LocationModel';
import { LocationButtonCard } from './LocationButtonCard';
import { LocationImagesCard } from './LocationImagesCard';
import { LocationOrderNotes } from './LocationOrderNotes';
import { LocationTagsCard } from 'components/location/cards/LocationTagsCard';
import { LocationWebLinks } from './LocationWebLinks';
import { isEmptyString } from 'lib/typeguards';
import { LocationRegularOpeningHoursCard } from './opening-hours/LocationRegularOpeningHoursCard';
import { LegacyCustomOpeningHours } from './opening-hours/LegacyCustomOpeningHoursCard';
import { LocationDateOverridesCard } from './opening-hours/LocationDateOverridesCard';

interface LocationCardsWrapperProps {
    location: Location;
    setCurrentLocation: (location: Location) => void;
}

export const LocationCardsWrapper = ({ location, setCurrentLocation }: LocationCardsWrapperProps) => {
    const { claims } = useAppSelector(state => state.auth);
    const { computedSettings } = useAppSelector(state => state.settings);
    const showWebLinks = React.useMemo(
        () => claims?.hasPermissionForLocation(location?._id, Resource.LocationWebLinks, Permission.read),
        [claims, location?._id]
    );
    const showLegacyCustomOpeningHours = React.useMemo(
        () => location.alternativeOpeningHoursText && !isEmptyString(location.alternativeOpeningHoursText),
        [location.alternativeOpeningHoursText]
    );
    const locationConfig = React.useMemo(
        () => getLocationConfigFromSettings(computedSettings),
        [computedSettings]
    );
    return (
        <React.Fragment>
            <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            lg: 4
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <LocationImagesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationGeneralCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationButtonCard
                                    location={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationTagsCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        size={{
                            lg: 4
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <LocationAddressCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationServicesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                    locationConfig={locationConfig}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationTablesAndZonesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            {showWebLinks && (
                                <Grid size={12}>
                                    <LocationWebLinks currentLocation={location} />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Grid
                        size={{
                            lg: 4
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <LocationRegularOpeningHoursCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationDateOverridesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            {showLegacyCustomOpeningHours && (
                                <Grid size={12}>
                                    <LegacyCustomOpeningHours
                                        currentLocation={location}
                                        setCurrentLocation={setCurrentLocation}
                                    />
                                </Grid>
                            )}
                            <Grid size={12}>
                                <LocationInAppLinksCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationOrderNotes
                                    setCurrentLocation={setCurrentLocation}
                                    currentLocation={location}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block', lg: 'none' } }}>
                <Grid container spacing={2}>
                    <Grid
                        size={{
                            md: 6
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <LocationGeneralCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationImagesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationServicesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                    locationConfig={locationConfig}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid
                        size={{
                            md: 6
                        }}
                    >
                        <Grid container spacing={2}>
                            <Grid size={12}>
                                <LocationAddressCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationRegularOpeningHoursCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            <Grid size={12}>
                                <LocationDateOverridesCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                            {showLegacyCustomOpeningHours && (
                                <Grid size={12}>
                                    <LegacyCustomOpeningHours
                                        currentLocation={location}
                                        setCurrentLocation={setCurrentLocation}
                                    />
                                </Grid>
                            )}
                            <Grid size={12}>
                                <LocationInAppLinksCard
                                    currentLocation={location}
                                    setCurrentLocation={setCurrentLocation}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Grid container spacing={2}>
                    <Grid size={12}>
                        <LocationGeneralCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationAddressCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationImagesCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationRegularOpeningHoursCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationDateOverridesCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    {showLegacyCustomOpeningHours && (
                        <Grid size={12}>
                            <LegacyCustomOpeningHours
                                currentLocation={location}
                                setCurrentLocation={setCurrentLocation}
                            />
                        </Grid>
                    )}
                    <Grid size={12}>
                        <LocationServicesCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                            locationConfig={locationConfig}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationInAppLinksCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationButtonCard location={location} setCurrentLocation={setCurrentLocation} />
                    </Grid>
                    <Grid size={12}>
                        <LocationTagsCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    <Grid size={12}>
                        <LocationTablesAndZonesCard
                            currentLocation={location}
                            setCurrentLocation={setCurrentLocation}
                        />
                    </Grid>
                    {showWebLinks && (
                        <Grid size={12}>
                            <LocationWebLinks currentLocation={location} />
                        </Grid>
                    )}
                    <Grid size={12}>
                        <LocationOrderNotes
                            setCurrentLocation={setCurrentLocation}
                            currentLocation={location}
                        />
                    </Grid>
                </Grid>
            </Box>
        </React.Fragment>
    );
};
